import { isObject } from '@vueuse/core'
import { type LoadingOptions, ElLoadingService } from 'element-plus'
import type { LoadingInstance } from 'element-plus/es/components/loading/src/loading'
import { Vue3Lottie } from 'vue3-lottie'
import type { Directive, DirectiveBinding, UnwrapRef, Ref } from 'vue'
import { isRef } from 'vue'

import IconLoader from '@/assets/loaders/icon-loader.json'

export default defineNuxtPlugin((nuxtApp) => {
  const INSTANCE_KEY = Symbol('ElLoading')

  type TLoadingBinding = boolean | UnwrapRef<LoadingOptions>
  interface IElementLoading extends HTMLElement {
    [INSTANCE_KEY]?: {
      instance: LoadingInstance
      options: LoadingOptions
    }
  }

  const createInstance = (
    el: IElementLoading,
    binding: DirectiveBinding<TLoadingBinding>
  ) => {
    function getBindingProp<K extends keyof LoadingOptions> (key: K): LoadingOptions[K] | undefined {
      return isObject(binding.value) ? (binding.value as LoadingOptions)[key] : undefined
    }

    const fullscreen = getBindingProp('fullscreen') ?? binding.modifiers.fullscreen

    const options: LoadingOptions = {
      text: h(Vue3Lottie, {
        animationData: IconLoader,
        autoPlay: true,
        loop: true,
        speed: 1
      }) as any,
      fullscreen,
      target: getBindingProp('target') ?? (fullscreen ? undefined : el),
      body: getBindingProp('body') ?? binding.modifiers.body,
      lock: getBindingProp('lock') ?? binding.modifiers.lock
    }
    el[INSTANCE_KEY] = {
      options,
      instance: ElLoadingService(options)
    }
  }

  const updateOptions = (
    newOptions: UnwrapRef<LoadingOptions>,
    originalOptions: LoadingOptions
  ) => {
    for (const key of Object.keys(originalOptions) as (keyof LoadingOptions)[]) {
      if (isRef(originalOptions[key])) {
        (originalOptions[key] as Ref).value = newOptions[key]
      }
    }
  }

  const LoadingCustomDirective: Directive<IElementLoading, TLoadingBinding> = {
    mounted (el, binding) {
      if (binding.value) {
        createInstance(el, binding)
      }
    },
    async updated (el, binding) {
      const instance = el[INSTANCE_KEY]
      if (binding.oldValue !== binding.value) {
        if (binding.value && !binding.oldValue) {
          createInstance(el, binding)
        } else if (binding.value && binding.oldValue) {
          if (isObject(binding.value)) {
            updateOptions(binding.value, instance!.options)
          }
        } else {
          el[INSTANCE_KEY]?.instance.close()
          instance?.instance.close()
        }
      }
    },
    async unmounted (el) {
      el[INSTANCE_KEY]?.instance.close()
      el[INSTANCE_KEY] = undefined
    }
  }

  nuxtApp.vueApp.directive('loading-custom', LoadingCustomDirective)
})
